import React, { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('[ErrorBoundary]', error, errorInfo);
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    const { error } = this.state;
    if (error && this.props.fallback) {
      return this.props.fallback(error, this.reset);
    }
    if (error) {
      return (
        <div className="p-6 rounded-xl border border-red-200 bg-red-50 text-red-800">
          <h3 className="font-semibold m-0 mb-2">Something went wrong</h3>
          <pre className="text-sm overflow-auto max-h-48 m-0 whitespace-pre-wrap">
            {error.message}
          </pre>
          {error.stack && (
            <details className="mt-2">
              <summary className="cursor-pointer text-sm">Stack trace</summary>
              <pre className="text-xs overflow-auto max-h-32 mt-1 m-0 whitespace-pre-wrap">
                {error.stack}
              </pre>
            </details>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}
